import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui';
import { EMOJI_COLOR_CLASSES } from '@/components/ui/EmojiPicker/EmojiPicker';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';

export interface ChildListItemProps {
  name: string;
  emoji?: string | null;
  color?: string | null;
  imagePath?: string | null;
  grade?: string | null;
  school?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function ChildListItem({
  name,
  emoji,
  color,
  imagePath,
  grade,
  school,
  onEdit,
  onDelete,
}: ChildListItemProps) {
  return (
    // 2px `border-strong` (#d1d1d1), not the 1px `border` (#e5e5e5) hairline:
    // QA filed the card outline as both too light and too faint to read
    // against the white surface (YAR-230). Kept identical to the member card
    // in Settings → Household members so the two rosters match.
    <div className="border-2 border-border-strong rounded-xl flex gap-2 items-center px-4 py-3">
      <ProfileAvatar
        imagePath={imagePath}
        emoji={emoji}
        color={color ?? EMOJI_COLOR_CLASSES[0]}
        initials={name?.charAt(0).toUpperCase()}
        alt={name}
        size="lg"
      />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <div className="flex gap-1 items-center">
          <span className="text-base font-medium text-foreground shrink-0">
            {name}
          </span>
          {grade && (
            <Badge
              variant="default"
              size="sm"
            >
              <span className="text-xs">{grade}</span>
            </Badge>
          )}
        </div>
        {school && (
          <span className="text-sm text-muted-foreground truncate">
            {school}
          </span>
        )}
      </div>
      {(onEdit || onDelete) && (
        <div className="flex items-center shrink-0">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="p-2.5 cursor-pointer"
              aria-label={`Edit ${name}`}
            >
              <Pencil
                size={24}
                className="text-muted-foreground"
              />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-2.5 cursor-pointer"
              aria-label={`Delete ${name}`}
            >
              <Trash2
                size={24}
                className="text-muted-foreground"
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
